import { Component, computed, effect, inject, signal, ViewChild } from '@angular/core';
import { ButtonComponent } from '../../../../kit/button/button.component';
import { CardModule } from '@openng/optimus-ui/card';
import { ActivatedRoute, Router } from '@angular/router';
import { isAllowed, routeParams, translate } from '../../../../../modules/shared/shared.global-service';
import { FolderSickNotesPageStore } from './folder-sick-notes-page.store';
import { TableColumn, TableColumnType } from '../../../../../modules/shared/components/kit/table/table.types';
import { boolColumn } from '../../../../../modules/shared/components/kit/table/table.fields';
import { TranslatePipe } from '@ngx-translate/core';
import {
    FolderSickNotesAssumeSickNoteModalComponent,
} from '../folder-sick-notes-assume-sick-note-modal/folder-sick-notes-assume-sick-note-modal.component';
import { FolderSickNotesObject } from '@libs/types/src/services/object-type/folder/folder-sick-notes.object';
import { folderSickNoteFrag } from '../../../../../services/types/folder-sick-notes.types';
import { TableComponent } from '../../../../../modules/shared/components/kit/table/table.component';
import { ServiceCertificateTypeEnum } from '@libs/types/src/services/enum/service-certificate-type.enum';
import {
    ServiceUploadCertificateModalComponent,
} from '../../../service/settings/service-upload-certificate-modal/service-upload-certificate-modal.component';
import { folderUri } from '../../../../../utils/router.utils';
import { BaseDialogService } from '../../../../kit/base-dialog/base-dialog-service';
import { LocalDatePipe } from '../../../../../modules/shared/pipes/local-date.pipe';

@Component({
    selector: 'app-folder-sick-notes-page',
    imports: [ButtonComponent, CardModule, TranslatePipe, TableComponent, LocalDatePipe],
    templateUrl: './folder-sick-notes-page.component.html',
    styleUrl: './folder-sick-notes-page.component.scss',
    providers: [FolderSickNotesPageStore],
})
export class FolderSickNotesPageComponent {
    private route = inject(ActivatedRoute);

    public readonly store = inject(FolderSickNotesPageStore);
    public readonly router = inject(Router);
    public readonly dialogService = inject(BaseDialogService);
    @ViewChild('sickNotesTable') sickNotesTable!: TableComponent;

    protected columns: TableColumn[] = [
        boolColumn('closed'),
        {
            name: 'incapacityStartDate',
            type: TableColumnType.Date,
        },
        {
            name: 'incapacityEndDate',
            type: TableColumnType.Date,
        },
        {
            name: 'decisionNumber',
            filterable: true,
        },
        {
            name: 'diagnosisCode',
        },
        {
            name: 'days',
            dataKey: null,
            data: (item) => {
                if (!item.incapacityStartDate || !item.incapacityEndDate) {
                    return null;
                }
                const dateFrom = new Date(item.incapacityStartDate);
                const dateTo = new Date(item.incapacityEndDate);
                const diffTime = Math.abs(dateTo.getTime() - dateFrom.getTime());
                return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            },
        },
    ];

    protected additionalFilter = computed(() => {
        return [[{
            column: 'folderId',
            operator: 'EQ',
            value: [routeParams.folderId()],
        }]];
    });

    protected selectedSickNote = signal<FolderSickNotesObject | null>(null);

    protected certExists = signal(false);

    protected canEdit = computed(() => isAllowed['sickNote.update']());
    protected canCreate = computed(() => isAllowed['sickNote.create']());

    constructor() {
        effect(async () => {
            await this.checkCertificates();
        });
    }

    protected selection(event: any) {
        this.selectedSickNote.set(event);
    }

    protected createSickNote() {
        this.router.navigate(['create'], { relativeTo: this.route }).then();
    }

    protected assumeSickNote() {
        const ref = this.dialogService.open(FolderSickNotesAssumeSickNoteModalComponent,
            {
                header: translate.instant('sickNotes.assumeSickNote.title'),
                styleClass: 'p-dialog-width-60',
            });
        ref.onClose.subscribe((result) => {
            if (result) {
                this.sickNotesTable.reload().then();
            }
        });
    }

    protected async checkCertificates() {
        if (this.store.folder()?.serviceId) {
            const res = await this.store.api.hasServiceCertificate({
                serviceId: this.store.folder()!.serviceId,
                type: ServiceCertificateTypeEnum.SUKL,
            });
            if (!res.ok) {
                const ref = this.dialogService.open(ServiceUploadCertificateModalComponent,
                    {
                        header: translate.instant('sickNotes.checkCertificates.title'),
                        data: {
                            serviceId: this.store.folder()!.serviceId,
                        },
                        styleClass: 'p-dialog-width-60',
                    });
                ref.onClose.subscribe(async (result) => {
                    if (result) {
                        this.certExists.set(result);
                    }
                });
            } else {
                this.certExists.set(true);
            }
        }
    }

    protected detail() {
        this.router.navigate([folderUri(routeParams.folderId(), this.store.folder()?.serviceId, `sick-notes/${this.selectedSickNote()?.id}`)]).then();
    }

    protected readonly isAllowed = isAllowed;

    protected readonly folderSickNoteFrag = folderSickNoteFrag;
}
